import React, { useState, useMemo } from 'react';
import {
  Box, Card, Typography, Avatar, Tooltip, useTheme, alpha,
  Accordion, AccordionSummary, AccordionDetails, IconButton
} from '@mui/material';
import {
  Functions as FunctionsIcon, ExpandMore as ExpandMoreIcon, ContentCopy as ContentCopyIcon,
  CheckCircleOutline as SuccessIcon, ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { copyToClipboard } from '../../../utils/clipboardUtils'; // Ensure this path is correct

const ToolReturnMessage = ({ toolName, content, timestamp, toolCallId }) => {
  const theme = useTheme();
  const [userHasToggledExpansion, setUserHasToggledExpansion] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).format(date);
  };

  const {
    isJson,
    status, // 'success', 'error', or null
    statusMessage, // The message to display, if any
    contentStringForCopy, // String for clipboard (pretty JSON or original string)
    contentStringForDisplay // String for display (pretty JSON or original string)
  } = useMemo(() => {
    let pContent = null;
    let json = false;
    let stat = null;
    let statMsg = null;
    let copyStr = content === null || content === undefined ? '' : String(content);
    let displayStr = content === null || content === undefined ? '' : String(content);

    try {
      if (typeof content === 'string' && (content.trim().startsWith('{') || content.trim().startsWith('['))) {
        pContent = JSON.parse(content);
        json = true;
      } else if (typeof content === 'object' && content !== null) {
        pContent = content; // Already an object
        json = true;
      }
      // Else, content is a non-JSON string, null, undefined, or other primitive.

      if (json && pContent) {
        copyStr = JSON.stringify(pContent, null, 2);
        displayStr = copyStr; // Display pretty JSON if it's JSON

        // Determine status and status message from parsed JSON content
        if (pContent && typeof pContent.status === 'string') {
          const S = String(pContent.status).toLowerCase();
          if (S === 'success' || S === 'error') {
            stat = S;
          }
        }

        if (stat === 'error' && pContent && typeof pContent.message === 'string') {
          statMsg = pContent.message;
        } else if (stat === 'success' && pContent && typeof pContent.summary_message === 'string') {
          statMsg = pContent.summary_message;
        } else if (stat === 'success' && pContent && typeof pContent.message === 'string') {
          // Fallback to 'message' for success if 'summary_message' isn't there
          statMsg = pContent.message;
        }
      }
    } catch (e) {
      console.warn("ToolReturnMessage: Content parsing issue. Displaying as string.", content, e);
      json = false; // Ensure json is false if parsing fails
      // Retain original strings for display/copy if parsing failed
      copyStr = content === null || content === undefined ? '' : String(content);
      displayStr = content === null || content === undefined ? '' : String(content);
    }
    return {
      isJson: json,
      status: stat,
      statusMessage: statMsg,
      contentStringForCopy: copyStr,
      contentStringForDisplay: displayStr
    };
  }, [content]);

  const isContentLarge = contentStringForDisplay.length > 200 || contentStringForDisplay.split('\n').length > 7;
  const isEffectivelyExpanded = !isContentLarge || userHasToggledExpansion;

  const handleAccordionChange = (event, newExpandedState) => {
    if (isContentLarge) {
      setUserHasToggledExpansion(newExpandedState);
    }
  };

  const handleCopyContent = (e) => {
    e.stopPropagation();
    copyToClipboard(contentStringForCopy, 'Tool result content copied!');
  };

  // Determine colors and icon based on status
  let avatarBgColor = alpha(theme.palette.grey[500], 0.7); // Default grey
  let cardBgColor = alpha(theme.palette.grey[500], 0.05);
  let cardBorderColor = alpha(theme.palette.grey[500], 0.2);
  let titleColor = 'text.secondary';
  let StatusPresentationIcon = FunctionsIcon; // Default icon

  if (status === 'success') {
    avatarBgColor = alpha(theme.palette.success.dark, 0.7);
    cardBgColor = alpha(theme.palette.success.main, 0.05);
    cardBorderColor = alpha(theme.palette.success.main, 0.2);
    titleColor = 'success.dark';
    StatusPresentationIcon = SuccessIcon;
  } else if (status === 'error') {
    avatarBgColor = alpha(theme.palette.error.dark, 0.7);
    cardBgColor = alpha(theme.palette.error.main, 0.05);
    cardBorderColor = alpha(theme.palette.error.main, 0.2);
    titleColor = 'error.dark';
    StatusPresentationIcon = ErrorIcon;
  }


  return (
    <Box sx={{ display: 'flex', alignSelf: 'flex-start', maxWidth: '80%', pl: { xs: 2, sm: 5 }, mt: 0.5, mb: 0.5 }}>
      <Box sx={{ mr: 1, mt: 0.5 }}>
        <Tooltip title={`Tool Result: ${toolName}${toolCallId ? ` (ID: ${toolCallId})` : ''}${status ? ` - ${status}` : ''}`}>
          <Avatar sx={{ bgcolor: avatarBgColor, width: 24, height: 24 }}>
            <StatusPresentationIcon fontSize="inherit" />
          </Avatar>
        </Tooltip>
      </Box>
      <Card sx={{ backgroundColor: cardBgColor, borderRadius: 1, flexGrow: 1, border: `1px solid ${cardBorderColor}` }}>
        <Accordion
          expanded={isEffectivelyExpanded}
          onChange={handleAccordionChange}
          disableGutters
          elevation={0}
          sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={isContentLarge ? <ExpandMoreIcon /> : null}
            aria-controls={`tool-return-content-${toolCallId || toolName}-${timestamp}`} // Unique ID
            id={`tool-return-header-${toolCallId || toolName}-${timestamp}`}      // Unique ID
            sx={{
              py: 0, px: 1.5, minHeight: 'auto',
              '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
              cursor: isContentLarge ? 'pointer' : 'default',
            }}
            onClick={!isContentLarge ? (e) => e.stopPropagation() : undefined}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Box sx={{ overflow: 'hidden', pr: 1 }}> {/* Container for text elements */}
                <Typography variant="caption" color={titleColor} fontWeight="bold" component="span">
                  Tool Result: {toolName}
                  {status && (
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, textTransform: 'capitalize', fontWeight: status === 'error' ? 'bold' : 'normal' }}>
                      ({status})
                    </Typography>
                  )}
                </Typography>
                {toolCallId && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 0.5, fontStyle: 'italic' }} component="span">
                    (ID: {toolCallId})
                  </Typography>
                )}
                {timestamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', ml: 1 }} component="span">
                    ({formatDate(timestamp)})
                  </Typography>
                )}
                {statusMessage && (
                     <Typography variant="caption" sx={{ 
                       display: 'block', 
                       fontSize: '0.7rem', 
                       mt:0.25, 
                       color: status === 'error' ? 'error.main' : 'text.secondary',
                       whiteSpace: 'normal', // Allow status message to wrap
                     }}>
                        {statusMessage.length > 100 ? statusMessage.substring(0,97) + "..." : statusMessage}
                     </Typography>
                )}
              </Box>
              {isEffectivelyExpanded && (
                <Tooltip title="Copy Content">
                  <IconButton
                    size="small"
                    onClick={handleCopyContent}
                    aria-label="Copy tool result content"
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          {isEffectivelyExpanded && (
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              {isJson ? (
                <SyntaxHighlighter
                  style={vs2015}
                  language="json"
                  customStyle={{ fontSize: '0.8rem', margin: '0.5rem 0 0 0', maxHeight: '250px', overflowY: 'auto', backgroundColor: 'transparent' }}
                  wrapLines={true}
                  PreTag="div"
                >
                  {contentStringForDisplay}
                </SyntaxHighlighter>
              ) : (
                <Typography variant="body2" component="pre" /* Using pre for better plain text formatting */ sx={{ 
                  mt: 0.5, 
                  whiteSpace: 'pre-wrap', 
                  wordBreak: 'break-word', 
                  fontSize: '0.8rem',
                  fontFamily: 'monospace', // Consistent font for code-like text
                  maxHeight: '250px',
                  overflowY: 'auto',
                }}>
                  {contentStringForDisplay || '(No content returned)'}
                </Typography>
              )}
            </AccordionDetails>
          )}
        </Accordion>
      </Card>
    </Box>
  );
};

export default ToolReturnMessage;